import { iMerchant } from "core/kangarooTypes/iMerhant";
import { iOrder } from "core/kangarooTypes/iOrder";
import { iPaginationResponse } from "core/kangarooTypes/iPaginationResponse";
import { iResponse } from "core/kangarooTypes/iResponse";
import { iOrderToAdd } from "core/kangarooTypes/OrderToAdd";
import { iBusinessFormData } from "core/kangarooTypes/ProfileManagement";
import ApiService from "./api";
import { iOrderDetails } from "core/kangarooTypes/iOrderDetails";
import { iPayment } from "core/kangarooTypes/iPayment";
import { toast } from "react-toastify";

export const getMerchant = (
  merchantId: string
): Promise<iResponse<iMerchant>> => {
  return ApiService.fetchData({
    url: `merchants/${merchantId}`,
    method: "GET",
  });
};

export const updateBusinessInfo = (
  data: iBusinessFormData,
  merchant_id: string
): Promise<any> => {
  return ApiService.fetchData({
    url: `merchants/${merchant_id}`,
    method: "PUT",
    data: data,
  });
};
export const addAddress = (
  data: {
    name: string;
    address?: string;
    lat: string;
    long: string;
  },
  merchant_id: string
): Promise<{
  data: {
    id: number;
    name: string;
    address: string;
    lat: string;
    long: string;
    primary: number;
  };
}> => {
  return ApiService.fetchData({
    url: `merchant/${merchant_id}/addresses`,
    method: "POST",
    data: data,
  });
};
export const updateAddress = (
  data: {
    name: string;
    address?: string;
    lat: string;
    long: string;
    primary: number;
  },
  address_id: string
): Promise<{
  data: {
    id: number;
    name: string;
    address: string;
    lat: string;
    long: string;
    primary: number;
  };
}> => {
  return ApiService.fetchData({
    url: `addresses/${address_id}`,
    method: "PUT",
    data: data,
  });
};
export const deleteAddress = (address_id: string): Promise<any> => {
  return ApiService.fetchData({
    url: `addresses/${address_id}`,
    method: "DELETE",
  });
};
export const setAddressAsPrimary = (
  data: {
    name: string;
    address: string;
    lat: string;
    long: string;
    primary: number;
  },
  address_id: string
): Promise<{
  data: {
    id: number;
    name: string;
    address: string;
    lat: string;
    long: string;
    primary: number;
  };
}> => {
  return ApiService.fetchData({
    url: `addresses/${address_id}`,
    method: "PUT",
    data: data,
  });
};

export const getOrders = (
  filter?: {
    order?: string | undefined;
    status?: string | undefined;
    orderType?: string | undefined;
    dateFrom?: string | undefined;
    dateTo?: string | undefined;
    driver_id?: string | undefined;
    merchant_id?: string | undefined;
    date_type?: string | undefined;
    sort_field?: string | undefined;
    sort_dir?: string | undefined;
    multiple_status?: string[] | undefined;
    export?: boolean | undefined;
    payment?: boolean | undefined;
  },
  page: number = 1
): Promise<iPaginationResponse<iOrder>> | Promise<void> => {
  if (filter?.export) {
    return ApiService.fetchData({
      url: `orders`,
      method: "GET",
      params: {
        search: filter?.order,
        from: filter?.dateFrom,
        to: filter?.dateTo,
        type: filter?.orderType,
        status: filter?.status,
        driver_id: filter?.driver_id,
        merchant_id: filter?.merchant_id,
        page: page,
        sort_field: filter?.sort_field,
        date_type: filter?.date_type || "created_at",
        sort_dir: filter?.sort_dir,
        export: filter?.export,
        payment: filter?.payment,
        multiple_status: filter?.multiple_status,
      },
      responseType: "blob", // Important: We need to handle binary data
    })
      .then((response) => {
        // Create a URL for the blob
        const url = window.URL.createObjectURL(
          new Blob([response], { type: "application/pdf" })
        );

        // Create a temporary link element
        const link = document.createElement("a");
        link.href = url;

        // Set the file name (you might want to get this from headers)
        link.setAttribute(
          "download",
          `orders-${new Date().toISOString().split("T")[0]}.xlsx`
        );

        // Append to body, click, and clean up
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      })
      .catch((error) => {
        toast.error("Failed to download payments invoice.");
      });
  } else {
    return ApiService.fetchData({
      url: `orders`,
      method: "GET",
      params: {
        search: filter?.order,
        from: filter?.dateFrom,
        to: filter?.dateTo,
        type: filter?.orderType,
        status: filter?.status,
        driver_id: filter?.driver_id,
        merchant_id: filter?.merchant_id,
        page: page,
        sort_field: filter?.sort_field,
        date_type: filter?.date_type || "created_at",
        sort_dir: filter?.sort_dir,
        multiple_status: filter?.multiple_status,
        payment: filter?.payment,
      },
    });
  }
};

export const addOrder = (data: any): Promise<any> => {
  // Pass the data exactly as constructed in OrderForm
  // No modifications - just send what we receive
  return ApiService.fetchData({
    url: `orders`,
    method: "POST",
    data: data,
  });
};
export const getOrderDetails = (
  orderId: string
): Promise<iResponse<iOrderDetails>> => {
  return ApiService.fetchData({
    url: `orders/${orderId}`,
    method: "GET",
  });
};

export const getPayments = (
  filter?: {
    order?: string | undefined;
    status?: string | undefined;
    orderType?: string | undefined;
    dateFrom?: string | undefined;
    dateTo?: string | undefined;
    driver_id?: string | undefined;
    merchant_id?: string | undefined;
  },
  page: number = 1
): Promise<iPaginationResponse<iPayment>> => {
  return ApiService.fetchData({
    url: `payments`,
    method: "GET",
    params: {
      search: filter?.order,
      from: filter?.dateFrom,
      to: filter?.dateTo,
      type: filter?.orderType,
      status: filter?.status,
      driver_id: filter?.driver_id,
      merchant_id: filter?.merchant_id,
      page: page,
    },
  });
};

export const downloadPaymentsExcel = (): Promise<void> => {
  return ApiService.fetchData({
    url: `payments/export`, // Assuming there's an export endpoint
    method: "GET",

    responseType: "blob", // Important: We need to handle binary data
  })
    .then((response) => {
      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response]));

      // Create a temporary link element
      const link = document.createElement("a");
      link.href = url;

      // Set the file name (you might want to get this from headers)
      link.setAttribute(
        "download",
        `payments-export-${new Date().toISOString().split("T")[0]}.xlsx`
      );

      // Append to body, click, and clean up
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    })
    .catch((error) => {
      toast.error("Failed to download payments report.");
    });
};

// ============ Order Import & Workflow Services ============

export interface ImportResult {
  batch_id: string;
  total_orders: number;
  ready_for_packaging: number;
  on_hold: number;
  errors: { order_id: number; message: string }[];
  orders: {
    order_id: number;
    external_order_number?: number | string;
    customer_name: string;
    status: string;
    items_count: number;
    available_items: number;
    missing_items: number;
    missing_skus: string[];
  }[];
}

export interface PackagingData {
  item_services: {
    item_id: number;
    services: { service_id: number; quantity: number }[];
  }[];
  order_services: number[];
}

export interface DeliveryMethodData {
  delivery_method: "roots" | "third_party";
  city_id?: number;
  area_id?: number;
}

export interface AuditLog {
  id: number;
  action: string;
  field_name?: string;
  old_value?: string;
  new_value?: string;
  metadata?: any;
  user_name: string;
  created_at: string;
}

export interface AdditionalService {
  id: number;
  name: string;
  description?: string;
  price: number;
  service_level: "item" | "order";
}

/**
 * Import orders from Excel file
 */
export const importOrders = (
  file: File,
  merchantId: number
): Promise<{ success: boolean; message: string; data: ImportResult }> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("merchant_id", merchantId.toString());

  return ApiService.fetchData({
    url: "orders/import",
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

/**
 * Get orders by import batch ID
 */
export const getOrdersByBatch = (batchId: string): Promise<any> => {
  return ApiService.fetchData({
    url: `orders/batch/${batchId}`,
    method: "GET",
  });
};

/**
 * Update packaging for an order (add services, complete packaging)
 */
export const updatePackaging = (
  orderId: number,
  data: PackagingData
): Promise<iResponse<iOrderDetails>> => {
  return ApiService.fetchData({
    url: `orders/${orderId}/packaging`,
    method: "PUT",
    data: data,
  });
};

/**
 * Set delivery method for an order
 */
export const setDeliveryMethod = (
  orderId: number,
  data: DeliveryMethodData
): Promise<iResponse<iOrderDetails>> => {
  return ApiService.fetchData({
    url: `orders/${orderId}/delivery-method`,
    method: "PUT",
    data: data,
  });
};

/**
 * Get order audit logs
 */
export const getAuditLogs = (
  orderId: number
): Promise<{ success: boolean; data: AuditLog[] }> => {
  return ApiService.fetchData({
    url: `orders/${orderId}/audit-logs`,
    method: "GET",
  });
};

/**
 * Cancel an order
 */
export const cancelOrder = (
  orderId: number
): Promise<iResponse<iOrderDetails>> => {
  return ApiService.fetchData({
    url: `orders/${orderId}/cancel`,
    method: "POST",
  });
};

/**
 * Get all additional services
 */
export const getAdditionalServices = (): Promise<{
  data: AdditionalService[];
}> => {
  return ApiService.fetchData({
    url: "additional-services",
    method: "GET",
  });
};

/**
 * Get pick and pack fees
 */
export const getPickPackFees = (): Promise<{
  data: {
    id: number;
    name: string;
    min_items: number;
    max_items: number;
    price: number;
  }[];
}> => {
  return ApiService.fetchData({
    url: "pick-pack-fees",
    method: "GET",
  });
};

/**
 * Download order import template
 */
export const downloadOrderTemplate = (): Promise<void> => {
  return ApiService.fetchData({
    url: "orders/import/template",
    method: "GET",
    responseType: "blob",
  })
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "order-import-template.xlsx");
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    })
    .catch(() => {
      toast.error("Failed to download template.");
    });
};
